import { ContactType, getAppointmentById, getNonMaterializedAppointment, getUser, getUserContacts, getUserProfile, getAppointmentBefore } from '@uplift/api';
import { useQuery } from '@tanstack/react-query';


export type SessionDetailsAppointmentArg = {
  id: string;
  isMaterialized: true;
} | {
  id: string;
  parentId: string;
  fromDatetime: string;
  isMaterialized: false;
};


const getMaterializedOrNotMaterializedAppointment = async (arg: SessionDetailsAppointmentArg) => {
  if (arg.isMaterialized === true) {
    return getAppointmentById(arg.id);
  } else if (arg.isMaterialized === false) {
    return getNonMaterializedAppointment(arg.parentId, arg.fromDatetime);
  }

  return null;
};


export const useSessionDetailsModalData = (appointment?: SessionDetailsAppointmentArg) => {
  const { data, isInitialLoading: isSessionDetailsLoading } = useQuery({
    queryKey: [ 'session-details', appointment ],
    queryFn: async () => {
      if (!appointment) {
        return null;
      }

      const appointmentResponse = await getMaterializedOrNotMaterializedAppointment(appointment);

      if (!appointmentResponse) {
        return null;
      }

      const _appointment = appointmentResponse.data;

      const [ patient, patientProfile, patientPhone ] = await Promise.all([
        getUser(_appointment.patientId),
        getUserProfile(_appointment.patientId),
        getUserContacts(_appointment.patientId).then(res => res.data.find(d => d.type === ContactType.phone)),

      ]);

      return {
        appointment: _appointment,
        patientDetails: {
          id: patient.data.id,
          firstName: patient?.data?.firstName ?? '',
          lastName: patient?.data?.lastName ?? '',
          preferredName: patientProfile?.data?.preferredName ?? '',
          status: patient?.data?.status,
          phone: patientPhone?.value ?? '',
        },
      };
    },
    enabled: !!appointment?.id
  });

  const { data: previousAppointment, isInitialLoading: isPreviousAppointmentLoading } = useQuery({
    queryKey: [ 'previous-session', appointment?.id ],
    queryFn: async () => {
      if (!data?.appointment?.patientId) {
        return null;
      }

      const prevAppointmentResponse = await getAppointmentBefore(data.appointment.patientId, data.appointment.fromDatetime);
      const prevAppointment = prevAppointmentResponse.data;

      if (!prevAppointment) {
        return null;
      }

      return {
        id: prevAppointment.id,
        fromDatetime: prevAppointment.fromDatetime,
        toDatetime: prevAppointment.toDatetime,
      };
    },
    enabled: !!data?.appointment?.patientId,
  });

  const isLoading = isPreviousAppointmentLoading
    || isSessionDetailsLoading;

  return {
    data: {
      ...data,
      previousAppointment,
    },
    isLoading,
  };
};
